import { useEffect, useRef } from "react";

/**
 * Emporia Brain v3.1, Phase 1 (P1.3) — the first real "was this genuinely
 * seen" primitive in this codebase. The two pre-existing IntersectionObserver
 * usages (ProductRail's lazy-load trigger, Reveal's scroll-in animation)
 * both fire on the FIRST intersection with no minimum exposure time — that
 * measures "rendered near the viewport," not "seen." A card that flashes
 * past during a fast scroll must not count as a viewable impression.
 *
 * Requires the element to stay at/above `threshold` intersection ratio for
 * at least `minDurationMs` continuously before calling `onViewable`; leaving
 * the threshold before that resets the timer. Fires at most once per mount
 * by default (viewability is a one-time fact for analytics, not a toggling
 * boolean) — pass `once: false` to re-arm on every qualifying view.
 *
 * `onViewable` is read via a ref internally, so callers can safely pass a
 * fresh inline function every render without re-creating the observer.
 */
export function useViewability(ref, onViewable, { threshold = 0.5, minDurationMs = 1000, once = true } = {}) {
  const timerRef = useRef(null);
  const firedRef = useRef(false);
  const callbackRef = useRef(onViewable);
  callbackRef.current = onViewable;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (once && firedRef.current) return;
        if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
          if (!timerRef.current) {
            timerRef.current = setTimeout(() => {
              timerRef.current = null;
              firedRef.current = true;
              callbackRef.current();
              if (once) observer.disconnect();
            }, minDurationMs);
          }
        } else {
          clearTimer();
        }
      },
      { threshold: [0, threshold] }
    );

    observer.observe(el);
    return () => {
      clearTimer();
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, threshold, minDurationMs, once]);
}
